import { forwardRef, useId, useState } from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "@/components/ui/Icon/Icon";
import styles from "./Input.module.css";

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  /** Left-aligned icon name (Lucide). */
  icon?: IconName;
  /** Static text rendered inside the left side, before the value. */
  prefix?: string;
  /** Right-side affordance. "show" renders an eye toggle for passwords. */
  suffix?: "show" | ReactNode;
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon, prefix, suffix, invalid, type = "text", className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [revealed, setRevealed] = useState(false);

  const isPasswordToggle = suffix === "show" && type === "password";
  const effectiveType = isPasswordToggle && revealed ? "text" : type;

  const wrapperClass = [styles.wrap, invalid ? styles.invalid : null, className]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClass}>
      {icon && (
        <span className={styles.icon} aria-hidden="true">
          <Icon name={icon} size={15} />
        </span>
      )}
      {prefix && <span className={styles.prefix}>{prefix}</span>}
      <input
        ref={ref}
        id={inputId}
        type={effectiveType}
        className={styles.input}
        aria-invalid={invalid || undefined}
        {...rest}
      />
      {isPasswordToggle && (
        <button
          type="button"
          className={styles.eye}
          onClick={() => setRevealed((v) => !v)}
          aria-label={revealed ? "Hide password" : "Show password"}
        >
          <Icon name={revealed ? "eye-off" : "eye"} size={15} />
        </button>
      )}
      {suffix && suffix !== "show" && <span className={styles.suffix}>{suffix}</span>}
    </div>
  );
});
